import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarBlank, PaperPlaneRight } from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { runtimeConfig } from "@/lib/config";
import { TurnstileWidget } from "@/components/site/TurnstileWidget";
import { BUDGETS, CONTACT_TIMES } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const inputBaseCls =
  "bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-gold focus-visible:ring-gold/30 rounded-sm";

export function LeadForm({ leadType = "enquiry", defaultProject = "", defaultProjectId = "", defaultUnitId = "", brochureSlug = "", onSuccess, showDate = false, showMessage = true, compact = false }) {
  const [projects, setProjects] = useState([]);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [projectsError, setProjectsError] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    project_of_interest: defaultProject, budget_range: "",
    preferred_contact_time: "", message: "", privacy_consent: false, marketing_email: false, marketing_whatsapp: false,
  });

  const inputCls = `${inputBaseCls} ${compact ? "h-10 sm:h-12" : "h-12"}`;
  const messageCls = `${inputBaseCls} ${compact ? "h-16 sm:h-24" : "h-24"}`;
  const gridCls = compact ? "grid gap-3 sm:grid-cols-2 sm:gap-4" : "grid gap-4 sm:grid-cols-2";
  const wantsDate = showDate || leadType === "site_visit";

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(r.data))
      .catch((requestError) => setProjectsError(formatApiErrorDetail(requestError.response?.data?.detail, requestError)));
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    if (!form.privacy_consent) { toast.error("Please accept the privacy consent to continue."); return; }
    setSubmitting(true);
    try {
      const query = new URLSearchParams(window.location.search);
      const selectedProject = projects.find((project) => project.name === form.project_of_interest);
      const payload = {
        name: form.name, phone: form.phone,
        email: form.email || null,
        project_id: defaultProjectId || selectedProject?.id || null,
        unit_id: defaultUnitId || null,
        project_of_interest: form.project_of_interest || null,
        budget_min: null, budget_max: null, configurations: [], preferred_locations: [], possession_by: null,
        purpose: "unspecified", priority: "normal", preferred_contact_time: form.preferred_contact_time || null,
        lead_type: leadType,
        preferred_date: wantsDate && date ? format(date, "yyyy-MM-dd") : null,
        message: form.message || null,
        source: query.get("utm_source") ? (query.get("utm_medium")?.includes("social") ? "paid_social" : "paid_search") : document.referrer ? "referral" : "website",
        utm_source: query.get("utm_source"), utm_medium: query.get("utm_medium"), utm_campaign: query.get("utm_campaign"), utm_term: query.get("utm_term"), utm_content: query.get("utm_content"),
        referrer: document.referrer || null, landing_page: window.location.href,
        consent: { privacy: form.privacy_consent, marketing_email: form.marketing_email, marketing_whatsapp: form.marketing_whatsapp, marketing_sms: false },
        turnstile_token: turnstileToken || null,
      };
      const { data } = await api.post("/crm/leads/capture", payload);
      toast.success(data.message || "Thank you! Our team will reach out shortly.");
      if (leadType === "brochure") {
        const url = `${API}/brochure${brochureSlug ? `?slug=${encodeURIComponent(brochureSlug)}` : ""}`;
        const a = document.createElement("a");
        a.href = url;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Your brochure is downloading…");
      }
      setForm({ name: "", phone: "", email: "", project_of_interest: defaultProject, budget_range: "", preferred_contact_time: "", message: "", privacy_consent: false, marketing_email: false, marketing_whatsapp: false });
      setDate(null);
      setTurnstileToken("");
      setTurnstileResetKey((value) => value + 1);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, err) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className={compact ? "space-y-3 sm:space-y-4" : "space-y-4"} data-testid="lead-form">
      <div className={gridCls}>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Full Name*</Label>
          <Input data-testid="lead-name" autoComplete="name" maxLength={100} required className={inputCls} value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Phone Number*</Label>
          <Input data-testid="lead-phone" type="tel" inputMode="tel" autoComplete="tel" maxLength={24} required pattern="[+()0-9 .-]{7,24}" className={inputCls} value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+91 ..." />
        </div>
      </div>

      <div className={gridCls}>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Email</Label>
          <Input data-testid="lead-email" type="email" autoComplete="email" maxLength={254} className={inputCls} value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="you@email.com" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Project of Interest</Label>
          <Select value={form.project_of_interest} onValueChange={set("project_of_interest")}>
            <SelectTrigger data-testid="lead-project" className={inputCls}><SelectValue placeholder="Select a project" /></SelectTrigger>
            <SelectContent className="bg-ink-2 border-white/10 text-white">
              {projects.map((p) => (
                <SelectItem key={p.slug} value={p.name} className="focus:bg-gold/20 focus:text-white">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {projectsError && <p className="text-xs text-red-200/80">Projects could not be loaded. You can still submit the form.</p>}
        </div>
      </div>

      {compact && <p className="text-[11px] leading-relaxed text-white/40 sm:hidden">We only ask for the essentials on mobile. Our team can collect extra preferences when they contact you.</p>}

      <div className={compact ? "hidden sm:contents" : "contents"}>
      <div className={gridCls}>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Budget Range</Label>
          <Select value={form.budget_range} onValueChange={set("budget_range")}>
            <SelectTrigger data-testid="lead-budget" className={inputCls}><SelectValue placeholder="Select budget" /></SelectTrigger>
            <SelectContent className="bg-ink-2 border-white/10 text-white">
              {BUDGETS.map((b) => <SelectItem key={b} value={b} className="focus:bg-gold/20 focus:text-white">{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Preferred Contact Time</Label>
          <Select value={form.preferred_contact_time} onValueChange={set("preferred_contact_time")}>
            <SelectTrigger data-testid="lead-time" className={inputCls}><SelectValue placeholder="Any time" /></SelectTrigger>
            <SelectContent className="bg-ink-2 border-white/10 text-white">
              {CONTACT_TIMES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {wantsDate && (
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Preferred Visit Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="lead-date" className={`${inputCls} flex w-full items-center gap-3 px-3 text-left ${date ? "text-white" : "text-white/30"}`}>
                <CalendarBlank size={18} className="text-gold" />
                {date ? format(date, "PPP") : "Pick a date"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto bg-ink-2 border-white/10 p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {showMessage && (
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs uppercase tracking-wider">Message</Label>
          <Textarea data-testid="lead-message" maxLength={2000} className={messageCls} value={form.message} onChange={(e) => set("message")(e.target.value)} placeholder="Tell us what you're looking for..." />
        </div>
      )}
      </div>

      <div className="space-y-2 text-xs text-white/55">
        <label className="flex items-start gap-2"><input type="checkbox" checked={form.privacy_consent} onChange={(e)=>set("privacy_consent")(e.target.checked)} className="mt-0.5 accent-gold"/><span>I consent to Aureon processing my information to respond to this enquiry. *</span></label>
        <label className={compact ? "hidden items-start gap-2 sm:flex" : "flex items-start gap-2"}><input type="checkbox" checked={form.marketing_email} onChange={(e)=>set("marketing_email")(e.target.checked)} className="mt-0.5 accent-gold"/><span>Send me relevant project updates by email.</span></label>
        <label className={compact ? "hidden items-start gap-2 sm:flex" : "flex items-start gap-2"}><input type="checkbox" checked={form.marketing_whatsapp} onChange={(e)=>set("marketing_whatsapp")(e.target.checked)} className="mt-0.5 accent-gold"/><span>Send updates and brochures through WhatsApp.</span></label>
      </div>

      <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileResetKey} />
      {runtimeConfig.turnstileSiteKey && !turnstileToken && (
        <p className="text-xs text-white/45">Complete the security check before submitting.</p>
      )}

      <button
        type="submit"
        disabled={submitting || Boolean(runtimeConfig.turnstileSiteKey && !turnstileToken)}
        data-testid="lead-submit"
        className="btn-gold group mt-2 inline-flex w-full items-center justify-center gap-3 rounded-sm border border-gold bg-gold px-8 py-4 text-sm font-medium uppercase tracking-widest text-ink transition-all duration-300 hover:bg-transparent hover:text-gold disabled:opacity-60"
      >
        {submitting ? "Sending..." : leadType === "site_visit" ? "Book My Site Visit" : "Submit Enquiry"}
        <PaperPlaneRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
}
